/**
 * ProtectedRoute Component
 * Guards routes that require authentication and optionally specific permissions
 */

import { Navigate, useLocation } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useOathAuth } from '../../services/oathAuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { DualRingSpinner } from '../ui/Spinner';

/**
 * ProtectedRoute - Wraps routes that require authentication
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string[]} [props.requiredPermissions] - Permission slugs required (any of these)
 * @param {string[]} [props.requiredAllPermissions] - Permission slugs required (all of these)
 * @param {string} [props.redirectTo] - Custom redirect path for unauthenticated users
 */
const ProtectedRoute = ({
  children,
  requiredPermissions,
  requiredAllPermissions,
  redirectTo = '/',
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useOathAuth();
  const { canAny, canAll } = usePermissions();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-950">
        <div className="text-center">
          <DualRingSpinner size="lg" className="mx-auto" />
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check permissions if specified
  const hasRequiredPermission = !requiredPermissions || canAny(requiredPermissions);
  const hasAllRequiredPermissions = !requiredAllPermissions || canAll(requiredAllPermissions);

  if (!hasRequiredPermission || !hasAllRequiredPermissions) {
    return <AccessDenied />;
  }

  return children;
};

/**
 * AccessDenied - Shown when user lacks required permissions
 */
const AccessDenied = () => {
  const { logout } = useOathAuth();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-sm w-full">
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <LockClosedIcon className="w-10 h-10 text-red-600 dark:text-red-400" />
          </div>

          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h1>

          <p className="text-gray-600 dark:text-gray-400 mb-8">
            You don&apos;t have permission to access this page.
            Please contact your administrator if you believe this is an error.
          </p>

          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={logout}
              className="px-4 py-2.5 bg-primary hover:bg-primary-dark text-white font-medium rounded-xl transition-colors shadow-sm"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * RequirePermission - Inline permission check for conditional rendering
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render if permitted
 * @param {string[]} [props.any] - Show if user has any of these permissions
 * @param {string[]} [props.all] - Show if user has all of these permissions
 * @param {React.ReactNode} [props.fallback] - Content to show if not permitted
 */
export const RequirePermission = ({ children, any, all, fallback = null }) => {
  const { canAny, canAll } = usePermissions();

  const hasAnyPermission = !any || canAny(any);
  const hasAllPermissions = !all || canAll(all);

  if (!hasAnyPermission || !hasAllPermissions) {
    return fallback;
  }

  return children;
};

export default ProtectedRoute;
